import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { GearHashtagItem, GearUploadStatusType } from '@/types/gears';
import { LabType } from '@/types/labs';

type GearLab = {
    labId: number;
    labName?: string;
    labAvatarUrl?: string;
    labType?: LabType;
};

type GearMeta = {
    gearTitle: string;
    gearDescription: string;
    isCommentAllowed: boolean;
    scoreSoftware: number;
    scoreHardware: number;
    scoreDaily: number;
    scoreInnovation: number;
    scoreSpecialized: number;
    scoreVersatile: number;
    gearHashtag1: GearHashtagItem;
    gearHashtag2: GearHashtagItem;
    gearHashtag3: GearHashtagItem;
};

type GearResource = {
    thumbnailUuid: string;
    thumbnailExtension: string;
    thumbnailResourceUrl: string;
    videoUuid?: string;
    videoExtension?: string;
    videoResourceUrl?: string;
    url?: string;
    uploadType: string;
};

type GearCode = {
    codeUuid: string;
    codeExtension: string;
    codeResourceUrl: string;
};

type GearUploadStore = {
    editGearId: number | null;
    gearStatus: GearUploadStatusType;
    isLabCompleted: boolean;
    isMetaCompleted: boolean;
    isResourceCompleted: boolean;
    isCodeCompleted: boolean;
    registeredGearLab: GearLab | null;
    registeredGearMeta: GearMeta | null;
    registeredGearResource: GearResource | null;
    registeredGearCode: GearCode | null;
    setEditGearId: (gearId: number) => void;
    setGearStatus: (status: GearUploadStatusType) => void;
    setIsLabCompleted: (isLabCompleted: boolean) => void;
    setIsMetaCompleted: (isMetaCompleted: boolean) => void;
    setIsResourceCompleted: (isResourceCompleted: boolean) => void;
    setIsCodeCompleted: (isCodeCompleted: boolean) => void;
    setRegisteredGearLab: (gearLab: GearLab) => void;
    setRegisteredGearMeta: (gearMeta: GearMeta) => void;
    setRegisteredGearResource: (gearResource: GearResource) => void;
    setRegisteredGearCode: (gearCode: GearCode) => void;
    reset: () => void;
};

const initialState = {
    editGearId: null,
    gearStatus: 'draft' as GearUploadStatusType,
    isLabCompleted: false,
    isMetaCompleted: false,
    isResourceCompleted: false,
    isCodeCompleted: false,
    registeredGearLab: null,
    registeredGearMeta: null,
    registeredGearResource: null,
    registeredGearCode: null,
};

export const useGearUploadStore = create<GearUploadStore>()(
    persist(
        (set) => ({
            editGearId: null,
            gearStatus: 'draft',
            isLabCompleted: false,
            isMetaCompleted: false,
            isResourceCompleted: false,
            isCodeCompleted: false,
            registeredGearLab: null,
            registeredGearMeta: null,
            registeredGearResource: null,
            registeredGearCode: null,
            setEditGearId: (value) => set({ editGearId: value }),
            setGearStatus: (status) => set({ gearStatus: status }),
            setIsLabCompleted: (flag) => set({ isLabCompleted: flag }),
            setIsMetaCompleted: (flag) => set({ isMetaCompleted: flag }),
            setIsResourceCompleted: (flag) => set({ isResourceCompleted: flag }),
            setIsCodeCompleted: (flag) => set({ isCodeCompleted: flag }),
            setRegisteredGearLab: (value) => set({ registeredGearLab: value }),
            setRegisteredGearMeta: (value) => set({ registeredGearMeta: value }),
            setRegisteredGearResource: (value) => set({ registeredGearResource: value }),
            setRegisteredGearCode: (value) => set({ registeredGearCode: value }),
            reset: () => {
                set(initialState);
            },
        }),
        {
            name: 'KEY-GEAR-UPLOAD-DATA',
            storage: typeof window !== 'undefined' ? createJSONStorage(() => localStorage) : undefined,
        },
    ),
);
